import React, { useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_ISOLATION,
  GET_ALL_ISOLATIONS,
  LINK_ISOLATION_JOBS,
  REMOVE_ISOLATION,
  UPDATE_ISOLATION,
} from '../../../graph-queries/tar/isolation';
import { TIsolation } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import IsolationForm from '../../organisms/IsolationForm/IsolationForm';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useIsFeatureFlagEnabled, useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import {
  ISOLATION_DRAWING_STATUS_ENUM,
  ISOLATION_LINE_WALK_PACK_STATUS_ENUM,
  ISOLATION_SOURCE_ENUM,
} from '../../../lib/constants/dbConstants';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { EMPTY_ISOLATION_FILTERS } from '../../../lib/constants';
import PermitFiltersModal from '../../organisms/PermitFiltersModal/PermitFiltersModal';
import IsolationFiltersModal from '../../organisms/IsolationFilter/IsolationFiltersModal';
import { isolationsGlobalFilterFn } from './Isolations.util';
import { GET_ALL_EVENTS } from '../../../graph-queries/tar/event';

const commaSeperatedValuesCell = <T, V>(context: CellContext<T, V>) => {
  return ((context.getValue() as string) || '').split(',').join(', ');
};

export const columns: ColumnDef<TIsolation>[] = [
  {
    header: 'LOSH Number',
    accessorKey: 'loshNumber',
    enableSorting: true,
  },
  {
    header: 'Title',
    accessorKey: 'title',
    enableSorting: true,
  },
  {
    header: 'Linked Jobs',
    cell: ({ row }) => {
      const jobIds = row.original.tarJobs?.map((j) => j.jobId).join(', ');
      return <div className="max-w-[120px] whitespace-normal break-words">{jobIds}</div>;
    },
  },
  {
    header: 'Drawing Status',
    accessorKey: 'drawingStatus',
    enableSorting: false,
    accessorFn: (row) => ISOLATION_DRAWING_STATUS_ENUM[row.drawingStatus],
  },
  {
    header: 'Field Walked?',
    accessorKey: 'fieldWalked',
    enableSorting: false,
    accessorFn: (row) => {
      return row.fieldWalked === true ? 'YES' : row.fieldWalked === false ? 'NO' : '';
    },
  },
  {
    header: 'Isolation Number',
    accessorKey: 'isolationNumber',
    enableSorting: false,
    cell: (context) => {
      const isolationNumber = context.getValue() as string;
      const formattedIsolationNumber = isolationNumber.replace('#', '%23');
      const url = `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${formattedIsolationNumber}?openerRegistrationNumber=Isolation`;

      return (
        <a href={url} target="_blank" rel="noreferrer">
          {isolationNumber}
        </a>
      );
    },
  },
  {
    header: 'IDP Status',
    accessorKey: 'ecowIsolation.statusComment',
  },
  {
    header: 'Required Trades',
    accessorKey: 'requiredTrades',
    enableSorting: false,
    cell: commaSeperatedValuesCell,
  },
  //hiding until we update table formatting
  // {
  //   header: 'Electrical Source',
  //   accessorKey: 'source',
  //   enableSorting: false,
  //   accessorFn: (row) => ISOLATION_SOURCE_ENUM[row.source],
  // },

  // {
  //   header: 'Process',
  //   accessorKey: 'ecowIsolation.processIsolaitonPoints',
  //   enableSorting: false,
  // },
  // {
  //   header: 'ELEC LV1',
  //   accessorKey: 'ecowIsolation.elecLV1IsolationPoints',
  //   enableSorting: false,
  // },
  // {
  //   header: 'Instrument',
  //   accessorKey: 'ecowIsolation.instrumentIsolationPoints',
  //   enableSorting: false,
  // },
  // {
  //   header: 'ELEC LV2',
  //   accessorKey: 'ecowIsolation.elecLV2IsolationPoints',
  //   enableSorting: false,
  // },
  // {
  //   header: 'ELEC HV',
  //   accessorKey: 'ecowIsolation.elecHVIsolationPoints',
  //   enableSorting: false,
  // },
  {
    header: 'Total Points',
    accessorKey: 'ecowIsolation.totalIsolationPoints',
    enableSorting: false,
  },
  {
    header: 'Line Walk Pack Status',
    accessorKey: 'lineWalkPackStatus',
    enableSorting: false,
    accessorFn: (row) => ISOLATION_LINE_WALK_PACK_STATUS_ENUM[row.lineWalkPackStatus],
  },
  {
    header: 'Pack Number',
    accessorKey: 'packNumber',
    enableSorting: false,
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const IsolationsPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const isFiltersEnabled = useIsFeatureFlagEnabled('FEATURE_FLAG_ENABLE_JOBS_FILTER');
  const [filters, setFilters] = useBrowserLocationState('filters', EMPTY_ISOLATION_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data, loading } = useQuery(GET_ALL_ISOLATIONS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const { data: events } = useQuery(GET_ALL_EVENTS);
  const isolationsWithEvent = useMemo(() => {
    if (!data?.allIsolations || !events?.allEvents) return [];

    // Create a lookup map for events by ID
    const eventMap = new Map(events.allEvents.map((event) => [event.eventId, event]));

    return data.allIsolations.map((isolation) => ({
      ...isolation,
      tarJobs: isolation?.tarJobs?.map((job) => ({
        ...job,
        tarEvent: job ? eventMap.get(job.eventId) : [],
      })),
    }));
  }, [data, events]);

  const [createIsolation, { loading: isCreating }] = useMutation(CREATE_ISOLATION, {
    refetchQueries: [GET_ALL_ISOLATIONS],
    awaitRefetchQueries: true,
  });
  const [updateIsolation, { loading: isUpdating }] = useMutation(UPDATE_ISOLATION, {
    refetchQueries: [GET_ALL_ISOLATIONS],
    awaitRefetchQueries: true,
  });
  const [linkIsolationJobs, { loading: isLinking }] = useMutation(LINK_ISOLATION_JOBS, {
    refetchQueries: [GET_ALL_ISOLATIONS],
    awaitRefetchQueries: true,
  });
  const [removeIsolation, { loading: isRemoving }] = useMutation(REMOVE_ISOLATION, {
    refetchQueries: [GET_ALL_ISOLATIONS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (isolation: TIsolation) => {
    const { isolationId, updatedAt, updatedBy, ...input } = isolation;
    return createIsolation({ variables: { input } }).then(() =>
      setAlertText('Isolation successfully added')
    );
  };
  const onEditSubmit = (isolation: TIsolation) => {
    const { updatedAt, updatedBy, ...input } = isolation;
    return updateIsolation({ variables: { input } }).then(() =>
      setAlertText('Isolation successfully updated')
    );
  };

  const onLinkSubmit = (selection: TIsolation[], jobIds: number[]) => {
    return Promise.all(
      selection.map((isolation) =>
        linkIsolationJobs({
          variables: {
            isolationId: isolation.isolationId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Isolation successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TIsolation[]) => {
    return Promise.all(
      selection.map((isolation) =>
        removeIsolation({
          variables: { isolationId: isolation.isolationId },
        })
      )
    ).then(() => setAlertText(`Isolation successfully deleted`));
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {isFilterOpen && (
          <IsolationFiltersModal
            defaultValues={filters}
            onSubmit={setFilters}
            onClose={() => setIsFilterOpen(false)}
          />
        )}
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TIsolation>
            hideSelectionColumn
            aspectName="isolation"
            data={(isolationsWithEvent as unknown as TIsolation[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <IsolationForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValues) => (
              <IsolationForm
                defaultValues={defaultValues}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the isolation tracker..."
            isLoading={isLoading}
            globalFilterFn={isolationsGlobalFilterFn}
            filters={filters}
            onFilterClick={(isFiltersEnabled && (() => setIsFilterOpen(true))) || undefined}
            onUpdateFilters={(newFilters) => setFilters(newFilters)}
          />
        )}
      </Container>
    </Layout>
  );
};

export default IsolationsPage;
